import { preloadThumbnailAsync, initializeInstance } from '../../Utils/InstanceInitializer';
import { preloadImageAsync } from '../../Utils/ImageLoader';
import { formatDate } from '../../Utils/Date';
import { checkASCIICookie, ASCII_ERROR } from '../../Utils/CookieHelper';

import { Report } from "../Button/Downloader/Reports/Report.js";

/**
 * @description Fetches the project data if able to
 * @param { ProjectInfo } projectInfo
 * @param { String } password
 * @returns { Report } A report containing the projectData on success
 */
async function getProjectAsync(projectInfo, password, consent) {
    let address = `${process.env.REACT_APP_GET_PROJECT_URL}id=${projectInfo.id}`;
    if (projectInfo.readonly)
        address += "&readOnly=true";

    let fetchOptions = {
        method: "POST",
        mode: 'cors',
        credentials: 'include',
        headers: {
            'Content-type': 'application/json; charset=UTF-8'
        },
        body : ""
    };
    if (consent) {
        fetchOptions.headers['Privacy-Policy-Consented'] = true;
    }
    if (password !== null && password !== undefined && password !== "") {
        fetchOptions.body = JSON.stringify({
            password: password
        });
    }
    let report = await fetch(address, fetchOptions)
        .then(async response => {
            const json = await response.json();

            if (!response.ok) {
                return Report.http(response.status, json);
            }

            return Report.http(200, json);
        }).catch(error => {
            console.log(error);
            //we encode cookies data in case of ASCII error
            if (error instanceof SyntaxError && error.message.includes("JSON")) {
                const isASCII = checkASCIICookie();
                if (!isASCII) return Report.http(error.code, ASCII_ERROR);
            } else {
                return Report.http(error.code);
            }         
        });
    return report;
}

/**
 * @description Loads a background style from a path
 * @param { URL } backgroundPath source url (undefined = default background)
 * @returns { {backgroundImage, opacity} } the style
 */
async function loadBackgroundAsync(backgroundPath) {
    try {
        const src = backgroundPath || 'assets/defaultBackground.jpg';

        await preloadImageAsync(src);

        /* Return the loaded background */
        return {
            backgroundImage: `url(${src})`,
            opacity: 1
        };
    } catch (error) {
        console.log("Failed to load background:");
        console.log(error);

        /* Return default background */
        return loadBackgroundAsync(undefined);
    }
}

/**
 * @description Main loading function that loads and formats a project
 * @param { ProjectInfo } projectInfo
 * @param { String } password
 */
export async function loadProjectAsync(projectInfo, password, consent) {
    /* Try to load the project */
    try {
        /* Invalid project */
        if (!projectInfo || !projectInfo.id) {
            return Report.http(406);        /* 'Not acceptable' code: cannot accept an invalid ID */
        }


        /* Fetch the project */
        let projectReport;
        projectReport = await getProjectAsync(projectInfo, password, consent);

        //Retry fetch if ASCII error
        if (projectReport.status === 1 && projectReport.data === ASCII_ERROR) {
            projectReport = await getProjectAsync(projectInfo, password, consent);
        }

        /* Unable to fetch */
        if (!projectReport.isSuccess()) {
            return projectReport;           /* Returns any possible http code */
        }

        const project = projectReport.data;

        /* Fetch successful, but invalid project */
        if (!(project && project.name && project.instances)) {
            return Report.http(410);        /* 'Gone' code: not the best code for the job, but it tells us that the project data is corrupted / invalid */
        }

        /* [ STEP 1 ] Load project background */

        let backgroundStyle = await loadBackgroundAsync(project.backgroundPath);

        /* [ STEP 2 ] Load instances */

        const thumbnailPreloadPromises = project.instances.map(instance => preloadThumbnailAsync(instance));

        const instances = await Promise.all(thumbnailPreloadPromises).then(results => {
            return results.filter(instance => instance && instance.publicWebUrl);
        });
       
        instances.forEach(i => initializeInstance(i));
        if (project.linkedInstances && project.linkedInstances.length > 0) project.linkedInstances.forEach(i => initializeInstance(i));    
        /* [ END ] */
        project.backgroundStyle = backgroundStyle;
        project.instances = instances;
        project.sharingDate = formatDate(project.date);

        return Report.data(project);
    }
    /* On fail, return the appropriate error */
    catch (error) {
        console.log("Error on project load:");
        console.log(error);

        return Report.ko();
    }
}
